/**
 * Iter-235m — Admin Shift Metrics Review page.
 *
 * Lists every submitted shift_metrics row with role, talent, key
 * numbers + Campaign Highlights at a glance. Admin can:
 *   - Approve         (status → 'approved')
 *   - Request clarification (status → 'needs_clarification' + message)
 *   - Mark reviewed    (legacy intermediate state)
 *
 * Filters: status pill + free-text search. No redesign — matches the
 * existing iEvntür admin table aesthetic.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Loader2, CheckCircle2, MessageSquareWarning, Search,
  RefreshCw, Sparkles, Image as ImageIcon, ChevronDown, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_PILLS = [
  { value: 'submitted',           label: 'Pending review', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'needs_clarification', label: 'Needs clarification', cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  { value: 'approved',            label: 'Approved', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { value: 'draft',               label: 'Drafts (incomplete)', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
];

const AdminShiftMetricsReview = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('submitted');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const load = async (status = filterStatus) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/shift-metrics?status=${status}&limit=200`);
      setRows(r.data.rows || []);
    } catch (err) {
      toast.error('Could not load metrics', { description: err?.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      (r.talent_email || '').toLowerCase().includes(q) ||
      (r.shift_title || '').toLowerCase().includes(q) ||
      (r.biggest_success || '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  return (
    <div className="p-4 max-w-6xl mx-auto" data-testid="admin-shift-metrics-review">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold">Campaign Metrics Review</h1>
          <p className="text-xs text-gray-500">Approve, clarify, or publish talent-submitted shift metrics.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => load(filterStatus)} disabled={loading} data-testid="review-refresh-btn">
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2 mb-3" data-testid="review-status-filters">
        {STATUS_PILLS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => { setFilterStatus(p.value); load(p.value); }}
            className={`text-xs px-3 py-1 rounded-full border ${
              filterStatus === p.value ? p.cls + ' font-semibold' : 'bg-white text-gray-600 border-gray-200 hover:border-cyan-300'
            }`}
            data-testid={`review-filter-${p.value}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-3 max-w-md">
        <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-gray-400" />
        <Input
          placeholder="Search talent email, shift, or highlight…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-7 h-9 text-xs"
          data-testid="review-search"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-cyan-500" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-gray-500">No metrics in this view.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((row) => (
            <MetricsRow
              key={row.id}
              row={row}
              expanded={expandedId === row.id}
              onToggle={() => setExpandedId(expandedId === row.id ? null : row.id)}
              onActionDone={() => load(filterStatus)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const MetricsRow = ({ row, expanded, onToggle, onActionDone }) => {
  const [clarifyOpen, setClarifyOpen] = useState(false);
  const [clarifyMsg, setClarifyMsg] = useState('');
  const [busy, setBusy] = useState(null);

  const approve = async () => {
    setBusy('approve');
    try {
      await api.post(`/admin/shift-metrics/${row.id}/approve`, { decision: 'approved' });
      toast.success('Approved');
      onActionDone();
    } catch (err) {
      toast.error('Approval failed', { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(null);
    }
  };

  const requestClarification = async () => {
    if (!clarifyMsg.trim()) return toast.error('Add a message for the talent');
    setBusy('clarify');
    try {
      await api.post(`/admin/shift-metrics/${row.id}/approve`, {
        decision: 'needs_clarification',
        message: clarifyMsg.trim(),
      });
      toast.success('Clarification requested');
      setClarifyOpen(false);
      setClarifyMsg('');
      onActionDone();
    } catch (err) {
      toast.error('Could not send clarification', { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(null);
    }
  };

  const statusPill = STATUS_PILLS.find((p) => p.value === row.status) || STATUS_PILLS[0];

  return (
    <Card data-testid={`review-row-${row.id}`}>
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <button type="button" onClick={onToggle} className="flex items-center gap-1 group text-left w-full"
                    data-testid={`review-expand-${row.id}`}>
              {expanded ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
              <p className="font-semibold text-sm text-gray-900 truncate group-hover:text-cyan-700">{row.shift_title}</p>
            </button>
            <p className="text-[11px] text-gray-500 ml-4">
              {row.talent_email} · <span className="font-mono">{row.role_category}</span> ·
              submitted {row.submitted_at ? new Date(row.submitted_at).toLocaleString() : '—'}
            </p>
            <div className="flex flex-wrap gap-3 mt-2 ml-4 text-[11px]">
              {[
                ['samples_distributed', 'Samples'],
                ['consumer_engagements', 'Engagements'],
                ['leads_collected', 'Leads'],
                ['qr_scans', 'QR'],
                ['email_signups', 'Emails'],
                ['booth_traffic', 'Traffic'],
              ].map(([k, lbl]) => row[k] != null && (
                <span key={k} className="text-gray-700">
                  <span className="text-gray-400">{lbl}:</span> <span className="font-semibold">{row[k]}</span>
                </span>
              ))}
              {(row.photos || []).length > 0 && (
                <span className="text-gray-500 inline-flex items-center gap-0.5">
                  <ImageIcon className="w-3 h-3" /> {row.photos.length}
                </span>
              )}
              {row.ai_extracted_from_voice && (
                <Badge className="bg-amber-100 text-amber-700 text-[10px] border border-amber-200">
                  <Sparkles className="w-2.5 h-2.5 mr-0.5" /> AI-prefilled
                </Badge>
              )}
            </div>
          </div>
          <Badge className={`text-[10px] border ${statusPill.cls}`} data-testid={`review-status-${row.id}`}>
            {statusPill.label}
          </Badge>
        </div>

        {expanded && (
          <div className="mt-3 ml-4 pl-3 border-l-2 border-cyan-200 space-y-3 text-xs" data-testid={`review-detail-${row.id}`}>
            {/* Campaign Highlights */}
            {(row.biggest_success || row.most_common_question || row.most_common_objection ||
              row.competitor_observations || row.recommendations || row.additional_observations) && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Campaign Highlights</p>
                <dl className="space-y-1.5">
                  {[
                    ['biggest_success',         'Biggest success'],
                    ['most_common_question',    'Most common Q'],
                    ['most_common_objection',   'Most common objection'],
                    ['competitor_observations', 'Competitor obs'],
                    ['recommendations',         'Recommendations'],
                    ['additional_observations', 'Other observations'],
                  ].map(([k, lbl]) => row[k] && (
                    <div key={k}>
                      <dt className="text-[10px] text-gray-500">{lbl}</dt>
                      <dd className="text-xs text-gray-800">{row[k]}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {/* AI qualitative — stored for Phase 3, surfaced here for admin context */}
            {(row.ai_consumer_sentiment || (row.ai_faqs || []).length || (row.ai_objections || []).length ||
              (row.ai_product_feedback || []).length || (row.ai_competitive_intel || []).length) && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 mb-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> AI qualitative extraction
                </p>
                {row.ai_consumer_sentiment && (
                  <p className="text-[11px]"><span className="text-gray-500">Sentiment:</span> <span className="font-medium">{row.ai_consumer_sentiment}</span></p>
                )}
                {(row.ai_faqs || []).length > 0 && (
                  <p className="text-[11px]"><span className="text-gray-500">FAQs:</span> {(row.ai_faqs || []).join(' · ')}</p>
                )}
                {(row.ai_objections || []).length > 0 && (
                  <p className="text-[11px]"><span className="text-gray-500">Objections:</span> {(row.ai_objections || []).join(' · ')}</p>
                )}
                {(row.ai_product_feedback || []).length > 0 && (
                  <p className="text-[11px]"><span className="text-gray-500">Product feedback:</span> {(row.ai_product_feedback || []).join(' · ')}</p>
                )}
                {(row.ai_competitive_intel || []).length > 0 && (
                  <p className="text-[11px]"><span className="text-gray-500">Competitive intel:</span> {(row.ai_competitive_intel || []).join(' · ')}</p>
                )}
              </div>
            )}

            {/* Photos thumbnails */}
            {(row.photos || []).length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
                  Photos · {(row.photos || []).length}
                </p>
                <div className="flex flex-wrap gap-2">
                  {(row.photos || []).slice(0, 10).map((ph, i) => (
                    <a key={i} href={ph.url} target="_blank" rel="noopener noreferrer"
                       className="relative w-16 h-16 rounded overflow-hidden border border-gray-200">
                      <img src={ph.url} alt="" className="w-full h-full object-cover" />
                      <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[8px] text-center truncate px-0.5">
                        {ph.category || ''}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Existing clarification message if any */}
            {row.status === 'needs_clarification' && row.clarification_message && (
              <div className="bg-rose-50 border border-rose-200 rounded p-2 text-[11px]">
                <p className="font-semibold text-rose-800 mb-0.5">Clarification requested</p>
                <p className="text-rose-700">{row.clarification_message}</p>
              </div>
            )}

            {/* Action bar */}
            {row.status !== 'approved' && (
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs" onClick={approve} disabled={!!busy} data-testid={`review-approve-${row.id}`}>
                  {busy === 'approve' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                  Approve
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => setClarifyOpen((v) => !v)} disabled={!!busy} data-testid={`review-clarify-${row.id}`}>
                  <MessageSquareWarning className="w-3 h-3 mr-1" />
                  {clarifyOpen ? 'Cancel' : 'Request clarification'}
                </Button>
              </div>
            )}
            {clarifyOpen && (
              <div className="space-y-2" data-testid={`review-clarify-form-${row.id}`}>
                <Textarea
                  rows={2}
                  placeholder="What needs clarification? e.g. 'Samples count looks too high — please double-check.'"
                  value={clarifyMsg}
                  onChange={(e) => setClarifyMsg(e.target.value)}
                  data-testid={`review-clarify-msg-${row.id}`}
                />
                <Button size="sm" className="h-8 text-xs" onClick={requestClarification} disabled={busy === 'clarify' || !clarifyMsg.trim()}>
                  {busy === 'clarify' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  Send clarification request
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminShiftMetricsReview;
